import { EnvironmentInjector, Injectable, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ReferenceDataService } from './reference-data.service';
import { Router } from '@angular/router';
import { initializeApp } from '../utils/utils';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

export enum ROLES {
  REGULAR = 'regular',
  ADVANCED = 'advanced',
  ADMIN = 'admin',
}


@Injectable({
  providedIn: 'root'
})
export class O365AuthService {
  private initialized = new BehaviorSubject<boolean>(false);
  readonly isInitialized$ = this.initialized.asObservable();
  private _signedIn = signal(false);
  private _username = signal<string | null>(null);
  private _userRoles = signal<string[]>([]);
  public viewAsRegular = signal(false);
  userRoles = this._userRoles.asReadonly();

  constructor(public msalService: MsalService,
    private injector: EnvironmentInjector,
    private referenceDataService: ReferenceDataService,
    private router: Router
  ) { }

  // called from main.ts
  initialize(): void {
    console.log('🚀 O365AuthService.initialize() triggered');
    this.msalService.instance.handleRedirectPromise().then(result => {
      // 1. Set active account
      if (result?.account) {
        console.debug('setting active account from redirect')
        this.msalService.instance.setActiveAccount(result.account);
      } else {
        console.debug('setting active account from cache')
        const cached = this.msalService.instance.getAllAccounts()[0];
        this.msalService.instance.setActiveAccount(cached ?? null);
      }

      this.syncState();

      const account = this.msalService.instance.getActiveAccount();
      // ✅ Initialize app only when we have an account
      if (account) {
        console.debug('✅ AuthService: Initializing app with active account');
        initializeApp(this.injector);
      } else {
        console.warn('⚠️ AuthService: No active account / MSAL session expired.');
        // Initialize app so unauthenticated views / error pages can render
        initializeApp(this.injector);
        // Trigger interactive re-login automatically when session expires
        // !! this should not be necessary since initializeApp is calling 
        // the backend which triggers the MSAL Guard
        // this.login();
      }
    }).catch(error => {
      console.error('⚠️ MSAL redirect error:', error);
      this.clearState();
      // If MSAL demands user interaction (expired refresh token / session cookie)
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('⚠️ AuthService: InteractionRequiredAuthError.');
        this.login();
      } else {
        // Initialize app anyway to avoid leaving the client in a frozen state
        initializeApp(this.injector);
      }
    }).finally(() => {
      // Signal readiness ONLY after handleRedirectPromise and initialization finish
      this.initialized.next(true);
    });
  }

  get signedIn(): boolean {
    return this._signedIn();
  }

  get username(): string | null {
    return this._username();
  }

  get isAdmin(): boolean {
    return this.userRoles().includes(ROLES.ADMIN);
  }

  get isActualAdmin(): boolean {
    return this.userRoles().includes(ROLES.ADMIN) && !this.viewAsRegular();
  }

  get isRegularUser(): boolean {
    return ((this.userRoles().length) == 1 && (this.userRoles()[0] === ROLES.REGULAR) ||
      (this.userRoles().includes(ROLES.ADMIN) && this.viewAsRegular()));
  }

  hasRole(role: string): boolean {
    // only consider viewAsRegular for admins -> remove admin and advanced
    console.debug('hasRole', role, this.userRoles(), this.viewAsRegular());
    if (this.userRoles().includes(ROLES.ADMIN) && this.viewAsRegular()) {
      return this.userRoles().filter(r => ((r !== ROLES.ADVANCED) && (r !== ROLES.ADMIN))).includes(role);
    } else {
      return this.userRoles().includes(role);
    }
  }

  hasAnyRole(roles?: string[]): boolean {
    let v: boolean = false;

    if (!roles || (roles.length === 0)) {
      return true;
    }
    // only consider viewAsRegular for admins
    if (this.userRoles().includes(ROLES.ADMIN) && this.viewAsRegular()) {
      v = this.userRoles().filter(r => ((r !== ROLES.ADVANCED) && (r !== ROLES.ADMIN))).some(r => roles.includes(r));
    } else {
      v = this.userRoles().some(r => roles.includes(r));
    }
    // console.debug('hasAnyRole', this.userRoles(), roles, this.viewAsRegular(), (v ? 'authorized' : 'unauthorized'));
    return v;
  }

  login(): void {
    console.warn('O365AuthService -> loginRedirect');
    this.msalService.loginRedirect({
      scopes: ['api://' + environment.azureAd.apiAppId + "/" + environment.azureAd.apiScope],
      prompt: 'select_account' // or 'login', 'consent', etc.
    });
  }

  logout(): void {
    console.debug("Logging out");
    const account = this.msalService.instance.getActiveAccount();
    console.log("Logging out account", account);
    this.msalService.instance.setActiveAccount(null);
    this.clearState();

    // Log out all accounts (there should only be one in practice)
    this.msalService.instance.clearCache()
  }

  syncState(): void {
    console.debug("Syncing auth state");
    const account = this.msalService.instance.getActiveAccount();
    // console.debug('account', account);
    // console.debug('idTokenClaims', account?.idTokenClaims);
    if (account) {
      this._signedIn.set(true);
      this._username.set(account.idTokenClaims?.['name'] || account.username);
      const idTokenClaims = account?.idTokenClaims as any;
      this._userRoles.set(idTokenClaims?.roles || []);
    } else {
      this.clearState();
    }
  }

  clearState(): void {
    console.debug("Clearing auth state");
    this._signedIn.set(false);
    this._username.set(null);
    this._userRoles.set([]);
  }
}
